// ImportWizard.js
//
// BYOP multi-step pipeline import wizard (unified ZIP + GitHub). Drafts are server-
// persisted and resumable. Steps:
//   1 Source & parse  ·  2 Inputs & parameters  ·  3 Dockerfile  ·  4 Entry command → finish
// Step 2 is a SINGLE table of detected values where the user marks each as an INPUT
// (a file uploaded at launch) or a PARAMETER (a value set at launch), with Add Input /
// Add Parameter. On save the combined list is split back into input_files + parameters
// (what the launch path, validator and BYOP launch page consume).
import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "../config";
import "./ImportWizard.css";

const STEPS = ["Source & parse", "Inputs & parameters", "Container image"];

async function apiJSON(url, opts = {}) {
    const res = await fetch(`${API_BASE}${url}`, { credentials: "include", ...opts });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
}

// Merge the parser's separate inputs[]/parameters[] into one combined list. Every detected
// row DEFAULTS to the "parameter" kind — the user explicitly switches a row to "input"
// (file) where needed.
function mergeDetected(inputs, parameters) {
    return [...(inputs || []), ...(parameters || [])].map((x) => ({
        kind: "param",
        name: x.name || "",
        flag: x.flag || "",
        required: false, // default unchecked on load; the user opts rows into "required"
        description: x.description || "",
        // choices/extensions are edited as RAW comma-separated TEXT in the row (so the
        // user can freely type commas/spaces); splitValues() parses them to arrays on save.
        extensions: Array.isArray(x.extensions) ? x.extensions.join(",") : (x.extensions || ""),
        type: x.type || "string",
        default: x.default == null ? "" : x.default,
        choices: Array.isArray(x.choices) ? x.choices.join(",") : (x.choices || ""),
    }));
}

export default function ImportWizard({ resumeByopId = null }) {
    const [step, setStep] = useState(1);
    const [byopId, setByopId] = useState(resumeByopId);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [dockerWarnings, setDockerWarnings] = useState([]);

    const [pipelineName, setPipelineName] = useState("");
    const [sourceTab, setSourceTab] = useState("zip");
    const [zipFile, setZipFile] = useState(null);
    const [githubUrl, setGithubUrl] = useState("");
    const [githubRef, setGithubRef] = useState("");     // optional branch/tag
    const [githubToken, setGithubToken] = useState(""); // optional PAT for private repos
    const [report, setReport] = useState(null);
    const [mainScript, setMainScript] = useState("");
    const [mainScriptCandidates, setMainScriptCandidates] = useState([]);

    const [values, setValues] = useState([]); // combined inputs + parameters
    const [dockerMode, setDockerMode] = useState("image"); // 'none' (container-complete) | 'image'
    const [imageUrl, setImageUrl] = useState("");

    const splitValues = useCallback(() => ({
        inputs: values.filter((v) => v.kind === "input").map((v) => ({ name: v.name, flag: v.flag, required: v.required, extensions: String(v.extensions || "").split(",").map((x) => x.trim()).filter(Boolean), description: v.description })),
        parameters: values.filter((v) => v.kind === "param").map((v) => ({ name: v.name, flag: v.flag, type: v.type, default: v.default, required: v.required, alwaysInclude: !!v.alwaysInclude, description: v.description, choices: String(v.choices || "").split(",").map((c) => c.trim()).filter(Boolean) })),
    }), [values]);

    const hydrate = useCallback((d) => {
        setByopId(d.byopId);
        setPipelineName(d.pipelineName || "");
        setReport(d.parse_report || null);
        setValues(mergeDetected(d.inputs, d.parameters));
        setImageUrl(d.imageUrl || "");
        setDockerMode(d.imageUrl ? "image" : (d.parse_report && d.parse_report.containersComplete ? "none" : "image"));
        setMainScript(d.main_script || "");
        setMainScriptCandidates((d.parse_report && d.parse_report.mainScriptCandidates) || []);
        if (d.import_step) setStep(Math.min(3, Math.max(1, d.import_step)));
    }, []);

    useEffect(() => {
        if (!resumeByopId) return;
        (async () => { try { hydrate(await apiJSON(`/byop/import/${resumeByopId}`)); } catch (e) { setError(e.message); } })();
    }, [resumeByopId, hydrate]);

    const saveStep = async (body) => {
        if (!byopId) return;
        await apiJSON(`/byop/import/${byopId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    };

    // ---- Step 1 ----
    const startImport = async () => {
        setError("");
        if (!/^[A-Za-z0-9_]{1,64}$/.test(pipelineName)) { setError("Pipeline name: 1–64 letters, numbers, underscores only."); return; }
        if (sourceTab === "zip" && !zipFile) { setError("Choose a .zip file to upload."); return; }
        if (sourceTab === "github" && !/^https:\/\/github\.com\/[\w.-]+\/[\w.-]+?(?:\.git|\/tree\/[\w./-]+)?\/?$/.test(githubUrl)) { setError("Enter a valid GitHub repo URL — https://github.com/owner/repo (optionally with /tree/<branch-or-tag>)."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("pipelineName", pipelineName);
            if (sourceTab === "zip") {
                fd.append("file", zipFile);
            } else {
                fd.append("githubUrl", githubUrl);
                if (githubRef.trim()) fd.append("ref", githubRef.trim());
                if (githubToken.trim()) fd.append("token", githubToken.trim());
            }
            const d = await apiJSON("/byop/import/start", { method: "POST", body: fd });
            setByopId(d.byopId);
            setReport(d.parse_report || {});
            setValues(mergeDetected(d.parse_report && d.parse_report.inputs, d.parse_report && d.parse_report.parameters));
            setMainScript(d.main_script || (d.parse_report && d.parse_report.mainScript) || "");
            setMainScriptCandidates((d.parse_report && d.parse_report.mainScriptCandidates) || []);
            if (d.parse_report && d.parse_report.containersComplete) setDockerMode("none");
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const chooseMain = async (v) => { setMainScript(v); if (v) { try { await saveStep({ main_script: v }); } catch (e) { setError(e.message); } } };

    const proceedFromStep1 = async () => {
        if (mainScriptCandidates.length > 1 && !mainScript) { setError("Please choose the main Nextflow script."); return; }
        setError(""); setBusy(true);
        try { await saveStep({ import_step: 2 }); setStep(2); } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    // ---- Steps 2–3 ----
    const goNext = async () => {
        setError(""); setBusy(true);
        try {
            if (step === 2) {
                const s = splitValues();
                if (s.inputs.concat(s.parameters).some((x) => !x.name || !x.flag)) throw new Error("Every row needs a name and a flag.");
                for (const p of s.parameters) {
                    if (p.type === "enum") {
                        if (!p.choices || !p.choices.length) throw new Error(`Parameter "${p.name}": enter comma-separated options (e.g. Y,N) for the enum.`);
                        if (p.default !== "" && p.default != null && !p.choices.map(String).includes(String(p.default)))
                            throw new Error(`Parameter "${p.name}": the default "${p.default}" must be one of the options (${p.choices.join(", ")}).`);
                    }
                    if (p.type === "list") {
                        if (!p.choices || !p.choices.length) throw new Error(`Parameter "${p.name}": enter comma-separated options for the list.`);
                        if (p.default !== "" && p.default != null) {
                            const toks = String(p.default).split(",").map((t) => t.trim()).filter(Boolean);
                            if (toks.some((t) => !p.choices.map(String).includes(t))) throw new Error(`Parameter "${p.name}": each list default must be one of the options (${p.choices.join(", ")}).`);
                        }
                    }
                    if (p.type === "path" && p.default !== "" && p.default != null) {
                        const v = String(p.default).trim();
                        if (!/^https?:\/\/.+/i.test(v) && !/^s3:\/\/[^/\s]+(\/.*)?$/i.test(v))
                            throw new Error(`Parameter "${p.name}": the default must be an http(s):// or s3:// path.`);
                    }
                }
                await saveStep({ inputs: s.inputs, parameters: s.parameters, import_step: 3 });
                setStep(3);
            }
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const goBack = () => { setError(""); setStep((s) => Math.max(1, s - 1)); };

    // Final step (Dockerfile): save the Dockerfile/image, then finalize the import. The
    // entry command is NOT authored here — it's built per-launch on the launch page.
    const finish = async () => {
        setError(""); setBusy(true);
        try {
            // "none" = container-complete pipeline: no image to save, just finalize.
            if (dockerMode === "image") {
                if (!imageUrl.trim()) throw new Error("Enter a container image URL.");
                await apiJSON(`/byop/import/${byopId}/dockerfile`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imageUrl }) });
            }
            await apiJSON(`/byop/import/${byopId}/finalize`, { method: "POST" });
            window.location.href = "/all-chats";
        } catch (e) { setError(e.message); setBusy(false); }
    };

    // ---- combined-table editing ----
    const updValue = (i, k, v) => setValues((a) => a.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    const addInput = () => setValues((a) => [...a, { kind: "input", name: "", flag: "", required: false, alwaysInclude: false, description: "", extensions: "", type: "string", default: "", choices: "" }]);
    const addParam = () => setValues((a) => [...a, { kind: "param", name: "", flag: "", required: false, alwaysInclude: false, description: "", extensions: "", type: "string", default: "", choices: "" }]);
    const removeValue = (i) => setValues((a) => a.filter((_, idx) => idx !== i));

    const loadParamsJson = (file) => {
        const rd = new FileReader();
        rd.onload = () => {
            try {
                const arr = JSON.parse(String(rd.result || "[]"));
                if (!Array.isArray(arr)) throw new Error("params.json must be a JSON array.");
                setValues((a) => [...a, ...arr.map((p) => ({ kind: "param", name: p.name || "", flag: p.flag || (p.name ? "--" + p.name : ""), required: !!p.required, alwaysInclude: !!p.alwaysInclude, description: p.description || "", extensions: "", type: p.type || "string", default: p.default == null ? "" : p.default, choices: Array.isArray(p.choices) ? p.choices.join(",") : (p.choices || "") }))]);
                setError("");
            } catch (e) { setError("params.json: " + e.message); }
        };
        rd.readAsText(file);
    };

    // reconciliation vs detected: flag any value the pipeline references that is no longer
    // in the table at all (kind is user-chosen, so we don't distinguish input/param here).
    const detectedNames = [
        ...(((report && report.inputs) || []).map((i) => i.name)),
        ...(((report && report.parameters) || []).map((p) => p.name)),
    ];
    const curNames = values.map((v) => v.name);
    const missingDetected = detectedNames.filter((n) => n && !curNames.includes(n));

    return (
        <div className="iw-wrap">
            <div className="iw-title">Import a pipeline</div>
            <div className="iw-sub">Bring your own Nextflow (DSL2) pipeline as a .zip or a public GitHub repo. We validate it, add our platform config, and make it launchable.</div>

            <div className="iw-steps">
                {STEPS.map((label, i) => {
                    const n = i + 1;
                    const cls = n === step ? "active" : n < step ? "done" : "";
                    return <div key={label} className={`iw-step ${cls}`}><span className="num">{n < step ? "✓" : n}</span>{label}</div>;
                })}
            </div>

            {error && <div className="iw-note err">{error}</div>}
            {dockerWarnings.length > 0 && (
                <div className="iw-note warn"><b>Dockerfile advisories:</b><ul style={{ margin: "6px 0 0 18px" }}>{dockerWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul></div>
            )}

            <div className="iw-panel">
                {step === 1 && (
                    <>
                        <div className="iw-h">1 · Source &amp; parse</div>
                        <div className="iw-field">
                            <label>Pipeline name</label>
                            <input className="iw-input" value={pipelineName} onChange={(e) => setPipelineName(e.target.value)} placeholder="my_rnaseq_pipeline" />
                        </div>
                        <div className="iw-tabs">
                            <div className={`iw-tab ${sourceTab === "zip" ? "active" : ""}`} onClick={() => setSourceTab("zip")}>Upload .zip</div>
                            <div className={`iw-tab ${sourceTab === "github" ? "active" : ""}`} onClick={() => setSourceTab("github")}>From GitHub</div>
                        </div>
                        {sourceTab === "zip" ? (
                            <div className="iw-field"><label>Pipeline .zip (your pipeline code: entry .nf + modules)</label><input type="file" accept=".zip" onChange={(e) => setZipFile(e.target.files[0] || null)} /></div>
                        ) : (
                            <>
                                <div className="iw-field"><label>GitHub repository URL</label><input className="iw-input" value={githubUrl} onChange={(e) => setGithubUrl(e.target.value)} placeholder="https://github.com/owner/repo  (or …/tree/<branch-or-tag>)" /></div>
                                <div className="iw-field"><label>Branch or tag (optional)</label><input className="iw-input" value={githubRef} onChange={(e) => setGithubRef(e.target.value)} placeholder="e.g. main, dev, 3.10.0 — overrides the URL's /tree" /></div>
                                <div className="iw-field"><label>Personal access token (optional — for private repos)</label><input className="iw-input" type="password" autoComplete="off" value={githubToken} onChange={(e) => setGithubToken(e.target.value)} placeholder="ghp_… / github_pat_…" /></div>
                                <div className="iw-note info">A token is only needed for <b>private</b> repositories. It is used once to download the code and is <b>never stored</b>. Use a classic PAT with the <code>repo</code> scope, or a fine-grained token with <b>Contents: read</b> for the repo.</div>
                            </>
                        )}
                        <div className="iw-note info"><b>Note:</b> Your pipeline's <code>nextflow.config</code> is <b>kept</b> and security-scanned — CloudGenomics layers its own execution settings (AWS Batch executor, queues, resource caps) on top at launch. You'll set the container and parameters in later steps.</div>

                        {report && <ParseReport report={report} />}
                        {report && mainScriptCandidates.length > 1 && (
                            <div className="iw-field" style={{ marginTop: 14 }}>
                                <label>Main Nextflow script — multiple .nf files were found; choose the entry script to run</label>
                                <select className="iw-select" value={mainScript} onChange={(e) => chooseMain(e.target.value)}>
                                    <option value="">-- Select the entry script --</option>
                                    {mainScriptCandidates.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}
                        {report && mainScriptCandidates.length === 1 && <div className="iw-note info" style={{ marginTop: 14 }}>Entry script detected: <b>{mainScript}</b></div>}
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className="iw-h">2 · Inputs &amp; parameters</div>
                        <div className="iw-sub">For each value your pipeline needs, choose whether it's an <b>Input</b> (a file the user uploads at launch) or a <b>Parameter</b> (a value set at launch). For parameters you can enter a <b>value now</b> — it's stored as the default and is shown &amp; editable on the launch page. These were auto-detected — adjust the kind, add, or remove as needed.</div>
                        <table className="iw-table">
                            <thead><tr><th>Kind</th><th>Name</th><th>Flag</th><th>Required</th><th title="Always include this parameter in the launch command, even at its default value (for a boolean, always emit the flag)">Add to Command</th><th>Details</th><th></th></tr></thead>
                            <tbody>
                                {values.map((r, i) => (
                                    <tr key={i}>
                                        <td>
                                            <select className="iw-select" value={r.kind} onChange={(e) => updValue(i, "kind", e.target.value)}>
                                                <option value="input">Input (file)</option>
                                                <option value="param">Parameter</option>
                                            </select>
                                        </td>
                                        <td><input className="iw-input" value={r.name} onChange={(e) => updValue(i, "name", e.target.value)} /></td>
                                        <td><input className="iw-input" value={r.flag} onChange={(e) => updValue(i, "flag", e.target.value)} placeholder={"--" + (r.name || "flag")} /></td>
                                        <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!r.required} onChange={(e) => updValue(i, "required", e.target.checked)} /></td>
                                        <td style={{ textAlign: "center" }}>
                                            {r.kind === "param"
                                                ? <input type="checkbox" checked={!!r.alwaysInclude} onChange={(e) => updValue(i, "alwaysInclude", e.target.checked)} title="Always include in the launch command" />
                                                : <span style={{ color: "#cbd5e1" }}>—</span>}
                                        </td>
                                        <td>
                                            {r.kind === "param" ? (
                                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                    <select className="iw-select" style={{ maxWidth: 100 }} value={r.type} onChange={(e) => updValue(i, "type", e.target.value)}>
                                                        {["string", "number", "boolean", "enum", "list", "path"].map((t) => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                    {(r.type === "enum" || r.type === "list") && (
                                                        <input className="iw-input" style={{ maxWidth: 150 }} value={r.choices || ""} onChange={(e) => updValue(i, "choices", e.target.value)} placeholder="options e.g. Y,N" />
                                                    )}
                                                    {r.type !== "boolean" && (
                                                        <input className="iw-input" value={r.default} onChange={(e) => updValue(i, "default", e.target.value)} placeholder={r.type === "enum" ? "default (one of options)" : r.type === "list" ? "default e.g. a,b,c" : r.type === "path" ? "https://… or s3://…" : "value (editable at launch)"} />
                                                    )}
                                                </div>
                                            ) : (
                                                <input className="iw-input" value={r.extensions || ""} onChange={(e) => updValue(i, "extensions", e.target.value)} placeholder="extensions e.g. fastq,gz" />
                                            )}
                                        </td>
                                        <td><button className="iw-btn ghost sm" onClick={() => removeValue(i)}>✕</button></td>
                                    </tr>
                                ))}
                                {values.length === 0 && <tr><td colSpan={7} style={{ color: "#94a3b8" }}>No values detected — add inputs/parameters below.</td></tr>}
                            </tbody>
                        </table>
                        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <button className="iw-btn ghost sm" onClick={addInput}>+ Add Input</button>
                            <button className="iw-btn ghost sm" onClick={addParam}>+ Add Parameter</button>
                            <label className="iw-link" style={{ marginLeft: "auto" }}>Upload params.json<input type="file" accept=".json" style={{ display: "none" }} onChange={(e) => e.target.files[0] && loadParamsJson(e.target.files[0])} /></label>
                        </div>
                        {missingDetected.length > 0 && <div className="iw-note warn" style={{ marginTop: 12 }}>Your pipeline references these values that aren't in the list: <b>{missingDetected.join(", ")}</b>. Add them as a Parameter or Input.</div>}
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className="iw-h">3 · Container image</div>
                        {report && report.containersComplete ? (
                            <div className="iw-sub">Every step in this pipeline already declares its own container, so <b>no image is needed</b> — CloudGenomics runs each step in its own container. You may still point to an existing image to override that.</div>
                        ) : (
                            <div className="iw-sub">This pipeline has step(s) without their own container, so one image is required. Point to an existing <b>container image</b> (Docker Hub, Quay.io, GHCR, or ECR). Images are used as-is — CloudGenomics does not build images from Dockerfiles.</div>
                        )}
                        <div className="iw-tabs">
                            {report && report.containersComplete && (
                                <div className={`iw-tab ${dockerMode === "none" ? "active" : ""}`} onClick={() => setDockerMode("none")}>No image needed (recommended)</div>
                            )}
                            <div className={`iw-tab ${dockerMode === "image" ? "active" : ""}`} onClick={() => setDockerMode("image")}>Use an existing image</div>
                        </div>
                        {dockerMode === "none" ? (
                            <div className="iw-note ok">Your pipeline is <b>container-complete</b>: {(report.processesTotal || 0)} step(s), each with its own container. The head job runs on the CloudGenomics Nextflow launcher and every step pulls its declared container. Click <b>Finish import</b>.</div>
                        ) : (
                            <>
                                <div className="iw-field">
                                    <label>Container image URL</label>
                                    <input className="iw-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="quay.io/biocontainers/samtools:1.19--h50ea8bc_0" />
                                </div>
                                <div className="iw-note warn">The image must be <b>publicly pullable</b> (or, for ECR, accessible to the CloudGenomics compute role). It is used as-is — not built or scanned by CloudGenomics.</div>
                            </>
                        )}
                    </>
                )}

                <div className="iw-actions">
                    <button className="iw-btn ghost" onClick={goBack} disabled={step === 1 || busy}>Back</button>
                    {step === 1 ? (
                        !report
                            ? <button className="iw-btn primary" onClick={startImport} disabled={busy}>{busy ? <span className="iw-spin" /> : "Parse pipeline"}</button>
                            : <button className="iw-btn primary" onClick={proceedFromStep1} disabled={busy || (mainScriptCandidates.length > 1 && !mainScript)}>{busy ? <span className="iw-spin" /> : "Continue"}</button>
                    ) : step < 3 ? (
                        <button className="iw-btn primary" onClick={goNext} disabled={busy}>{busy ? <span className="iw-spin" /> : "Save & continue"}</button>
                    ) : (
                        <button className="iw-btn primary" onClick={finish} disabled={busy}>{busy ? <span className="iw-spin" /> : "Finish import"}</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ParseReport({ report }) {
    const flags = report.resourceFlags || [];
    const over = flags.filter((f) => f.over);
    const dyn = flags.filter((f) => f.dynamic);
    const queues = report.queueFlags || [];
    const configScan = report.configScan || null;
    return (
        <div style={{ marginTop: 18 }}>
            <div className="iw-note ok">Parsed your pipeline. Detected <b>{(report.inputs || []).length}</b> input file(s) and <b>{(report.parameters || []).length}</b> parameter(s) across <b>{(report.processes || []).length}</b> process(es) — review them on the next step.</div>
            {(report.warnings || []).map((w, i) => <div key={i} className="iw-note warn">{w}</div>)}
            {over.length > 0 && (
                <div className="iw-note err"><b>Resource limits exceeded</b> — CloudGenomics caps each process at 32 vCPU / 96 GB; these requests will be clamped:
                    <ul style={{ margin: "6px 0 0 18px" }}>{over.map((f, i) => <li key={i}><code>{f.process}</code>: {f.reason}</li>)}</ul>
                </div>
            )}
            {dyn.length > 0 && <div className="iw-note warn">Dynamic resource directives in {dyn.map((f) => f.process).join(", ")} — capped to 32 vCPU / 96 GB at runtime.</div>}
            {queues.length > 0 && <div className="iw-note warn"><b>Queue directives found</b> in {queues.map((q) => q.process).join(", ")} — ignored; the platform assigns queues automatically.</div>}
            {configScan && <div className="iw-note info"><b>Config files kept &amp; security-scanned</b> ({configScan.scanned} checked). Your pipeline's <code>nextflow.config</code> is preserved; CloudGenomics layers its AWS Batch execution settings on top at launch.</div>}
        </div>
    );
}
